import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { FileText, Download, Building2, Calendar, CheckCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MeuContrato = () => {
  const { user, company } = useAuth();
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [contractData, setContractData] = useState(null);

  useEffect(() => {
    fetchContractData();
  }, []);

  const fetchContractData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/contract/data`);
      setContractData(response.data);
    } catch (error) {
      console.error('Error fetching contract data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/api/contract/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Contrato_${company?.nome?.replace(/\s+/g, '_') || 'Empresa'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Contrato baixado com sucesso!');
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error('Erro ao baixar contrato');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return 'N/A';
    const cleaned = cnpj.replace(/\D/g, '');
    if (cleaned.length === 14) {
      return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    return cnpj;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meu-contrato-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Meu Contrato</h1>
          <p className="text-slate-600">Contrato de Prestação de Serviços CERP Brasil NR1</p>
        </div>
        <Button
          onClick={handleDownloadPdf}
          disabled={downloadingPdf}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="download-contract-btn"
        >
          {downloadingPdf ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Gerando PDF...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Baixar Contrato em PDF
            </>
          )}
        </Button>
      </div>

      {/* Contract Info Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Building2 className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Contratante</p>
                <p className="font-semibold text-slate-800">{company?.nome || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">CNPJ</p>
                <p className="font-semibold text-slate-800">{formatCNPJ(company?.cnpj)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Data de Adesão</p>
                <p className="font-semibold text-slate-800">{formatDate(company?.created_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contract Document */}
      <Card className="shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-3 text-xl">
            <FileText className="h-6 w-6" />
            Contrato de Prestação de Serviços
          </CardTitle>
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-green-500">Vigente</Badge>
            <span className="text-slate-300 text-sm">Versão 1.0</span>
          </div>
        </CardHeader>
        <CardContent className="p-8 prose prose-slate max-w-none">
          <p className="text-center text-slate-600 mb-8">
            <strong>CONTRATO DE PRESTAÇÃO DE SERVIÇOS DE SOFTWARE COMO SERVIÇO (SaaS)</strong>
          </p>

          <p className="text-slate-600">
            Pelo presente instrumento particular, de um lado:
          </p>

          <div className="bg-slate-50 p-4 rounded-lg my-4 border-l-4 border-teal-500">
            <p className="text-slate-700 mb-0">
              <strong>CONTRATANTE:</strong> <span className="text-teal-700">{company?.nome || '[NOME DA EMPRESA]'}</span>,
              pessoa jurídica de direito privado, inscrita no CNPJ sob o nº <span className="text-teal-700">{formatCNPJ(company?.cnpj) || '[CNPJ]'}</span>,
              representada neste ato por <span className="text-teal-700">{user?.nome || '[REPRESENTANTE LEGAL]'}</span>.
            </p>
          </div>

          <p className="text-slate-600">
            E, de outro lado:
          </p>

          <div className="bg-slate-50 p-4 rounded-lg my-4 border-l-4 border-slate-500">
            <p className="text-slate-700 mb-0">
              <strong>CONTRATADA:</strong> Karina Di Paula Santana Soares LTDA, pessoa jurídica de direito privado,
              inscrita no CNPJ sob nº 66.298.458/0001-87, Inscrição Municipal nº 2798824,
              com sede na Rua Paes Leme, 215, Conj 1713 - Pinheiros, São Paulo - SP, CEP 05424-150,
              Nome Fantasia: Ogunhê Desenvolvimento de Sistemas, doravante denominada simplesmente "CERP BRASIL NR1".
            </p>
          </div>

          <h2 className="text-lg font-bold text-slate-800 mt-8">CLÁUSULA 1ª - DO OBJETO</h2>
          <p className="text-slate-600">
            1.1. O presente contrato tem por objeto a prestação de serviços de software como serviço (SaaS)
            para gestão de Segurança e Saúde no Trabalho (SST), através da plataforma CERP Brasil NR1,
            incluindo as funcionalidades de:
          </p>
          <ul className="text-slate-600">
            <li>Gerenciamento de Riscos Ocupacionais (GRO)</li>
            <li>Programa de Gerenciamento de Riscos (PGR)</li>
            <li>Avaliação Ergonômica Preliminar (AEP)</li>
            <li>Avaliação de Riscos Psicossociais</li>
            <li>Gestão de Funcionários e Treinamentos</li>
            <li>Geração de Relatórios e Documentos</li>
            <li>Integração com e-Social</li>
            <li>Análise Inteligente de Riscos com IA</li>
          </ul>

          <h2 className="text-lg font-bold text-slate-800">CLÁUSULA 2ª - DO PRAZO</h2>
          <p className="text-slate-600">
            2.1. O presente contrato vigorará por prazo indeterminado, a partir da data de adesão à plataforma,
            podendo ser rescindido por qualquer das partes mediante aviso prévio de 30 (trinta) dias.
          </p>
          <p className="text-slate-600">
            2.2. A CONTRATANTE possui direito a 7 (sete) dias de teste gratuito a partir da data de cadastro.
          </p>

          <h2 className="text-lg font-bold text-slate-800">CLÁUSULA 3ª - DO VALOR E FORMA DE PAGAMENTO</h2>
          <p className="text-slate-600">
            3.1. Pelos serviços prestados, a CONTRATANTE pagará à CONTRATADA o valor correspondente
            ao plano escolhido, conforme tabela de preços vigente na plataforma.
          </p>
          <p className="text-slate-600">
            3.2. O pagamento deverá ser realizado mensalmente, até o dia do vencimento da fatura,
            através dos meios de pagamento disponibilizados na plataforma.
          </p>

          <h2 className="text-lg font-bold text-slate-800">CLÁUSULA 4ª - DAS OBRIGAÇÕES DA CONTRATADA</h2>
          <p className="text-slate-600">4.1. São obrigações da CONTRATADA:</p>
          <ul className="text-slate-600">
            <li>Disponibilizar a plataforma com uptime mínimo de 99,5%</li>
            <li>Manter os dados da CONTRATANTE seguros e em conformidade com a LGPD</li>
            <li>Fornecer suporte técnico em horário comercial</li>
            <li>Realizar atualizações e melhorias contínuas na plataforma</li>
            <li>Manter backup dos dados por período mínimo de 30 dias</li>
          </ul>

          <h2 className="text-lg font-bold text-slate-800">CLÁUSULA 5ª - DAS OBRIGAÇÕES DA CONTRATANTE</h2>
          <p className="text-slate-600">5.1. São obrigações da CONTRATANTE:</p>
          <ul className="text-slate-600">
            <li>Realizar os pagamentos nas datas acordadas</li>
            <li>Utilizar a plataforma em conformidade com os Termos de Uso</li>
            <li>Manter seus dados cadastrais atualizados</li>
            <li>Não compartilhar credenciais de acesso com terceiros não autorizados</li>
            <li>Garantir que os dados inseridos na plataforma são verdadeiros e precisos</li>
          </ul>

          <h2 className="text-lg font-bold text-slate-800">CLÁUSULA 6ª - DA PROTEÇÃO DE DADOS</h2>
          <p className="text-slate-600">
            6.1. As partes se comprometem a cumprir todas as disposições da Lei Geral de Proteção
            de Dados (Lei nº 13.709/2018 - LGPD).
          </p>
          <p className="text-slate-600">
            6.2. A CONTRATADA atuará como operadora dos dados pessoais tratados na plataforma,
            seguindo estritamente as instruções da CONTRATANTE enquanto controladora.
          </p>

          <h2 className="text-lg font-bold text-slate-800">CLÁUSULA 7ª - DA RESCISÃO</h2>
          <p className="text-slate-600">
            7.1. O presente contrato poderá ser rescindido por qualquer das partes, mediante
            comunicação escrita com antecedência mínima de 30 (trinta) dias.
          </p>
          <p className="text-slate-600">
            7.2. Em caso de inadimplência superior a 30 dias, a CONTRATADA poderá suspender
            o acesso à plataforma até regularização do pagamento.
          </p>

          <h2 className="text-lg font-bold text-slate-800">CLÁUSULA 8ª - DISPOSIÇÕES GERAIS</h2>
          <p className="text-slate-600">
            8.1. Este contrato é regido pelas leis da República Federativa do Brasil.
          </p>
          <p className="text-slate-600">
            8.2. Fica eleito o foro da comarca de São Paulo/SP para dirimir quaisquer dúvidas
            ou litígios oriundos do presente contrato.
          </p>

          <div className="mt-8 p-6 bg-teal-50 rounded-lg border border-teal-200">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="h-5 w-5 text-teal-600" />
              <span className="font-semibold text-teal-800">Aceite Digital</span>
            </div>
            <p className="text-sm text-teal-700 mb-2">
              Este contrato foi aceito digitalmente através da plataforma CERP Brasil NR1.
            </p>
            <p className="text-sm text-teal-600">
              <strong>Data do aceite:</strong> {formatDate(contractData?.terms_accepted_at || company?.created_at)}<br/>
              <strong>Usuário responsável:</strong> {user?.email}<br/>
              <strong>Versão dos termos:</strong> {contractData?.terms_version || '1.0'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Related Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Documentos Relacionados</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="/termos-de-uso"
              target="_blank"
              className="flex items-center gap-3 p-4 border rounded-lg hover:bg-slate-50 transition-colors"
              data-testid="link-termos-uso"
            >
              <FileText className="h-5 w-5 text-slate-600" />
              <div>
                <p className="font-medium text-slate-800">Termos de Uso</p>
                <p className="text-sm text-slate-500">Condições gerais de uso da plataforma</p>
              </div>
            </a>
            <a
              href="/politica-de-privacidade"
              target="_blank"
              className="flex items-center gap-3 p-4 border rounded-lg hover:bg-slate-50 transition-colors"
              data-testid="link-politica-privacidade"
            >
              <FileText className="h-5 w-5 text-slate-600" />
              <div>
                <p className="font-medium text-slate-800">Política de Privacidade</p>
                <p className="text-sm text-slate-500">Como tratamos seus dados pessoais</p>
              </div>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MeuContrato;
